/**
 * Product Search API Client
 * Server'da Laravel'e direkt, client'ta Next API (self-request yok).
 */

import { fetchLaravelAPI, fetchNextAPI, parseLaravelJsonResponse } from '../api-helpers';

/** Sunucu tarafında listing/filtre isteklerine tag + revalidate (invalidation ile uyum) */
export type ProductSearchServerCacheOptions = {
  revalidate?: number;
  tags?: string[];
};

function productSearchFetch(url: string, serverCache?: ProductSearchServerCacheOptions): Promise<Response> {
  if (typeof window === 'undefined') {
    const hasCustom =
      (serverCache?.tags && serverCache.tags.length > 0) || serverCache?.revalidate != null;
    if (hasCustom) {
      return fetchLaravelAPI(url, {
        mode: 'page',
        next: {
          revalidate: serverCache?.revalidate ?? 180,
          ...(serverCache?.tags?.length ? { tags: serverCache.tags } : {}),
        },
      });
    }
    return fetchLaravelAPI(url, { mode: 'page' });
  }
  return fetchNextAPI(url);
}
import type {
    SearchParams,
    SearchResponse,
    FilterConfig,
    AutocompleteParams,
    AutocompleteResponse,
    CategoryProductsResponse,
    ApiErrorResponse,
} from '@/types/product-search';

/**
 * Laravel GET /api/products/search — successResponse: { success: true, data: { data: T[], meta } }.
 * Düz { data: T[] } için de uyumludur.
 */
export function extractProductsFromLaravelSearchResponse(json: unknown): unknown[] {
  if (!json || typeof json !== 'object') return [];
  const root = json as Record<string, unknown>;
  const payload = root.data;
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object' && 'data' in payload) {
    const inner = (payload as { data?: unknown }).data;
    if (Array.isArray(inner)) return inner;
  }
  return [];
}

/**
 * Dinamik ürün arama ve filtreleme
 * 
 * @param params - Arama parametreleri
 * @returns Ürün listesi ve sayfalama bilgileri
 */
export async function searchProducts(
  params: SearchParams,
  serverCache?: ProductSearchServerCacheOptions
): Promise<SearchResponse> {
    const searchParams = new URLSearchParams();
    
    // Zorunlu parametreler
    if (!params.product_type) {
        throw new Error('product_type parameter is required');
    }
    searchParams.set('product_type', params.product_type);
    
    // Opsiyonel parametreler
    if (params.category_slug) {
        searchParams.set('category_slug', params.category_slug);
    }
    if (params.language) {
        searchParams.set('language', params.language);
    }
    if (params.page) {
        searchParams.set('page', String(params.page));
    }
    if (params.per_page) {
        searchParams.set('per_page', String(params.per_page));
    }
    if (params.filters && Object.keys(params.filters).length > 0) {
        searchParams.set('filters', JSON.stringify(params.filters));
    }
    if (params.sort_by) {
        searchParams.set('sort_by', params.sort_by);
    }
    if (params.calculate_prices) {
        searchParams.set('calculate_prices', 'true');
    }
    if (params.departure_date) {
        searchParams.set('departure_date', params.departure_date);
    }
    if (params.check_in_date) {
        searchParams.set('check_in_date', params.check_in_date);
    }
    if (params.check_out_date) {
        searchParams.set('check_out_date', params.check_out_date);
    }
    if (params.pax_count) {
        searchParams.set('pax_count', String(params.pax_count));
    }
    if (params.child_count) {
        searchParams.set('child_count', String(params.child_count));
    }
    if (params.child_ages && params.child_ages.length > 0) {
        searchParams.set('child_ages', JSON.stringify(params.child_ages));
    }
    if (params.target_currency_id) {
        searchParams.set('target_currency_id', String(params.target_currency_id));
    }
    
    const url = `/products/search?${searchParams.toString()}`;
    const response = await productSearchFetch(url, serverCache);
    
    // 404 durumunda: Backend'den gelen JSON'u parse et ve boş data ile döndür
    // Bu durum "ürün bulunamadı" anlamına gelir ve normal bir durumdur
    if (!response.ok && response.status === 404) {
        try {
            const clonedResponse = response.clone();
            await clonedResponse.json();
            
            // Ürün bulunamadı - bu normal bir durum, boş array döndür
            const emptyResponse: SearchResponse = {
                success: false,
                data: [],
                meta: {
                    pagination: {
                        page: params.page || 1,
                        per_page: params.per_page || 12,
                        total: 0,
                        last_page: 0,
                    },
                },
            };
            return emptyResponse;
        } catch {
            // JSON parse edilemezse, boş response döndür
            const emptyResponse: SearchResponse = {
                success: false,
                data: [],
                meta: {
                    pagination: {
                        page: params.page || 1,
                        per_page: params.per_page || 12,
                        total: 0,
                        last_page: 0,
                    },
                },
            };
            return emptyResponse;
        }
    }
    
    const result = await parseLaravelJsonResponse<{ success: boolean; data: SearchResponse } | SearchResponse>(response, 'Product Search API');
    
    // Response formatı kontrolü - Backend'den gelen format: { success: true, data: {...} }
    if (typeof result === 'object' && result !== null && 'success' in result && result.success && 'data' in result && result.data) {
        // Backend response formatı: { success: true, data: { data: [...], meta: {...} } }
        // success property'sini koruyarak döndür
        const data = result.data as SearchResponse;
        return {
            success: result.success,
            data: data.data ?? [],
            meta: data.meta ?? {
                pagination: {
                    page: 1,
                    per_page: 12,
                    total: 0,
                    last_page: 0,
                },
            },
        } as SearchResponse;
    }
    
    // Eğer data direkt SearchResponse formatındaysa (fallback)
    if (typeof result === 'object' && result !== null && ('data' in result || 'meta' in result)) {
        // success property'si yoksa ekle
        if (!('success' in result)) {
            return {
                success: true,
                data: (result as SearchResponse).data ?? [],
                meta: (result as SearchResponse).meta ?? {
                    pagination: {
                        page: 1,
                        per_page: 12,
                        total: 0,
                        last_page: 0,
                    },
                },
            } as SearchResponse;
        }
        return result as SearchResponse;
    }
    
    // Hata durumu
    throw new Error('Invalid search products response format');
}

/**
 * URL/template ürün tipini filtre API'sinin kullandığı type_key'e eşler.
 * Sadece category_slug verilmediğinde kullanılır (geriye dönük uyumluluk).
 */
const FILTER_TYPE_KEY_MAP: Record<string, string> = {
    'private-tours': 'tour',
    'group-tours': 'tour',
};

function getFilterTypeKey(typeKey: string): string {
    return FILTER_TYPE_KEY_MAP[typeKey] ?? typeKey;
}

/** Aynı (type, dil, kategori) için tekrarlayan getProductFilters isteklerini birleştirir (MainSearchBar / useProductSearch / HomeHeaderSearchBar). */
const productFiltersResolvedCache = new Map<string, FilterConfig>();
const productFiltersInflight = new Map<string, Promise<FilterConfig>>();

function productFiltersCacheKey(
    apiTypeKey: string,
    lang: string,
    categorySlug: string | null | undefined,
    serverCache?: ProductSearchServerCacheOptions,
): string {
    const sc =
        serverCache?.revalidate != null || (serverCache?.tags && serverCache.tags.length > 0)
            ? `r:${serverCache.revalidate ?? ''}:${(serverCache.tags ?? []).join(',')}`
            : 'default';
    return `${apiTypeKey}|${lang}|${categorySlug ?? ''}|${sc}`;
}

/**
 * Ürün tipine veya kategoriye göre filtre yapılandırması
 * category_slug verilirse backend kategoriden ürün tipini çözer (dinamik); URL segmenti değişse bile doğru filtreler gelir.
 *
 * @param typeKey - Ürün tipi anahtarı (tour, private-tours, yacht, vb.) – category_slug yoksa kullanılır
 * @param language - Dil kodu (opsiyonel)
 * @param categorySlug - Kategori slug (opsiyonel); verilirse bu kategorinin bağlı olduğu ürün tipinin filtreleri döner
 * @returns Filtre yapılandırması (search_fields ve sidebar_filters)
 */
export async function getProductFilters(
    typeKey: string,
    language?: string,
    categorySlug?: string | null,
    serverCache?: ProductSearchServerCacheOptions
): Promise<FilterConfig> {
    const apiTypeKey = getFilterTypeKey(typeKey || 'tour');
    const lang = language && language.trim() ? language.trim() : 'en';
    const cacheKey = productFiltersCacheKey(apiTypeKey, lang, categorySlug, serverCache);
    const cached = productFiltersResolvedCache.get(cacheKey);
    if (cached) {
        return cached;
    }
    const inflight = productFiltersInflight.get(cacheKey);
    if (inflight) {
        return inflight;
    }

    const searchParams = new URLSearchParams();
    searchParams.set('language', lang);
    if (categorySlug && categorySlug.trim()) {
        searchParams.set('category_slug', categorySlug.trim());
    }

    const url = `/product-types/${encodeURIComponent(apiTypeKey)}/filters?${searchParams.toString()}`;
    const promise = (async (): Promise<FilterConfig> => {
    try {
        const response = await productSearchFetch(url, serverCache);
        
        if (response.ok) {
            const result = await parseLaravelJsonResponse<{ success: boolean; data: FilterConfig } | FilterConfig>(response, 'Product Filters API');
            if (typeof result === 'object' && result !== null && 'success' in result && result.success && 'data' in result && result.data) {
                return result.data as FilterConfig;
            }
            if (typeof result === 'object' && result !== null && ('search_fields' in result || 'sidebar_filters' in result)) {
                return result as FilterConfig;
            }
        }

        // İstek başarısızsa veya 404: type 'tour' ile bir kez daha dene (backend bazen 404 verebiliyor)
        if (!response.ok) {
            const fallbackUrl = `/product-types/tour/filters?language=${encodeURIComponent(lang)}`;
            const fallbackResponse = await productSearchFetch(fallbackUrl, serverCache);
            if (fallbackResponse.ok) {
                try {
                    const fallbackResult = await parseLaravelJsonResponse<{ success: boolean; data: FilterConfig } | FilterConfig>(fallbackResponse, 'Product Filters API (fallback)');
                    if (typeof fallbackResult === 'object' && fallbackResult !== null && 'success' in fallbackResult && (fallbackResult as { success?: boolean }).success && 'data' in fallbackResult) {
                        const data = (fallbackResult as { data?: FilterConfig }).data;
                        if (data && (Array.isArray(data.sidebar_filters) || Array.isArray(data.search_fields))) {
                            return data;
                        }
                    }
                    if (typeof fallbackResult === 'object' && fallbackResult !== null && ('search_fields' in fallbackResult || 'sidebar_filters' in fallbackResult)) {
                        return fallbackResult as FilterConfig;
                    }
                } catch {
                    // fallback parse hatası
                }
            }
        }
        
        return { search_fields: [], sidebar_filters: [] };
    } catch {
        return { search_fields: [], sidebar_filters: [] };
    }
    })();

    productFiltersInflight.set(cacheKey, promise);
    promise
        .then((config) => {
            productFiltersResolvedCache.set(cacheKey, config);
        })
        .finally(() => {
            productFiltersInflight.delete(cacheKey);
        });

    return promise;
}

/**
 * Otomatik tamamlama için öneriler
 * 
 * @param params - Autocomplete parametreleri
 * @returns Öneri listesi
 */
export async function getAutocompleteSuggestions(
    params: AutocompleteParams,
    getVariable?: (key: string, fallback: string) => string
): Promise<AutocompleteResponse['suggestions']> {
    const searchParams = new URLSearchParams();
    
    // Zorunlu parametreler
    searchParams.set('product_type', params.product_type);
    searchParams.set('field_key', params.field_key);
    searchParams.set('query', params.query);
    
    // Opsiyonel parametreler
    if (params.language) {
        searchParams.set('language', params.language);
    }
    if (params.sources) {
        // Çoklu source desteği (virgülle ayrılmış: "category,product")
        searchParams.set('sources', params.sources);
    } else if (params.source) {
        // Tek source desteği (geriye dönük uyumluluk)
        searchParams.set('source', params.source);
    }
    if (params.limit) {
        searchParams.set('limit', String(Math.min(Math.max(params.limit, 1), 50))); // 1-50 arası
    }
    if (params.root_category_id != null && params.root_category_id > 0) {
        searchParams.set('root_category_id', String(params.root_category_id));
    }
    
    const url = `/products/autocomplete?${searchParams.toString()}`;
    
    try {
        const response = await productSearchFetch(url);
        // 404 hatalarını sessizce handle et - boş array döndür
        if (response.status === 404) {
            return [];
        }
        const result = await parseLaravelJsonResponse<AutocompleteResponse>(
            response, 
            'Autocomplete API',
            getVariable
        );
        return result.suggestions || [];
    } catch (error) {
        // 404 hatalarını sessizce handle et
        if (error instanceof Error && error.message.includes('404')) {
            return [];
        }
        // Diğer hataları fırlat (component seviyesinde gösterilecek)
        throw error;
    }
}

/**
 * Kategoriye göre ürünler
 * 
 * @param categorySlug - Kategori slug'ı
 * @param params - Arama parametreleri (product_type otomatik kategori tipinden gelecek)
 * @returns Kategori bilgisi, ürün listesi ve sayfalama bilgileri
 */
export async function getCategoryProducts(
    categorySlug: string,
    params: Omit<SearchParams, 'category_slug' | 'product_type'> & { product_type?: string },
    serverCache?: ProductSearchServerCacheOptions
): Promise<CategoryProductsResponse> {
    const searchParams = new URLSearchParams();
    
    // product_type parametresi kritik - kategori tipini belirler
    if (params.product_type) {
        searchParams.set('product_type', params.product_type);
    }
    
    // Opsiyonel parametreler
    if (params.language) {
        searchParams.set('language', params.language);
    }
    if (params.page) {
        searchParams.set('page', String(params.page));
    }
    if (params.per_page) {
        searchParams.set('per_page', String(params.per_page));
    }
    if (params.filters && Object.keys(params.filters).length > 0) {
        searchParams.set('filters', JSON.stringify(params.filters));
    }
    if (params.sort_by) {
        searchParams.set('sort_by', params.sort_by);
    }
    if (params.calculate_prices) {
        searchParams.set('calculate_prices', 'true');
    }
    if (params.departure_date) {
        searchParams.set('departure_date', params.departure_date);
    }
    if (params.check_in_date) {
        searchParams.set('check_in_date', params.check_in_date);
    }
    if (params.check_out_date) {
        searchParams.set('check_out_date', params.check_out_date);
    }
    if (params.pax_count) {
        searchParams.set('pax_count', String(params.pax_count));
    }
    if (params.child_count) {
        searchParams.set('child_count', String(params.child_count));
    }
    if (params.child_ages && params.child_ages.length > 0) {
        searchParams.set('child_ages', JSON.stringify(params.child_ages));
    }
    if (params.target_currency_id) {
        searchParams.set('target_currency_id', String(params.target_currency_id));
    }
    if (params.featured_only) {
        searchParams.set('featured_only', 'true');
    }
    
    const url = `/categories/${encodeURIComponent(categorySlug)}/products${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    const response = await productSearchFetch(url, serverCache);
    
    // 404 durumunda: Backend'den gelen JSON'u parse et ve boş data ile döndür
    // Bu durum "ürün bulunamadı" veya "kategori bulunamadı" anlamına gelebilir
    if (!response.ok && response.status === 404) {
        try {
            // Response'u clone edip okuyoruz (response sadece bir kez okunabilir)
            const clonedResponse = response.clone();
            const errorData = await clonedResponse.json();
            
            // Ürün bulunamadı veya kategori bulunamadı - her iki durumda da boş array döndür
            // (Kullanıcıya "ürün bulunamadı" mesajı gösterilecek)
            const emptyResponse: CategoryProductsResponse = {
                success: false,
                category: null,
                data: [],
                meta: {
                    pagination: {
                        page: params.page || 1,
                        per_page: params.per_page || 12,
                        total: 0,
                        last_page: 0,
                    },
                },
            };
            return emptyResponse;
        } catch {
            // JSON parse edilemezse veya response zaten okunmuşsa, boş response döndür
            const emptyResponse: CategoryProductsResponse = {
                success: false,
                category: null,
                data: [],
                meta: {
                    pagination: {
                        page: params.page || 1,
                        per_page: params.per_page || 12,
                        total: 0,
                        last_page: 0,
                    },
                },
            };
            return emptyResponse;
        }
    }
    
    // Diğer hatalar için parseLaravelJsonResponse kullan (500, 403 vb.)
    const result = await parseLaravelJsonResponse<{ success: boolean; data: CategoryProductsResponse } | CategoryProductsResponse>(response, 'Category Products API');
    
    // Response formatı kontrolü - Backend'den gelen format: { success: true, data: {...} }
    if (typeof result === 'object' && result !== null && 'success' in result && result.success && 'data' in result && result.data) {
        // Backend response formatı: { success: true, data: { category, data, meta } }
        // success property'sini koruyarak döndür
        const data = result.data as CategoryProductsResponse;
        return {
            success: result.success,
            category: data.category ?? null,
            data: data.data ?? [],
            meta: data.meta ?? {
                pagination: {
                    page: 1,
                    per_page: 12,
                    total: 0,
                    last_page: 0,
                },
            },
        } as CategoryProductsResponse;
    }
    
    // Eğer data direkt CategoryProductsResponse formatındaysa (fallback)
    if (typeof result === 'object' && result !== null && ('category' in result || 'data' in result)) {
        // success property'si yoksa ekle
        return {
            success: ('success' in result && result.success) ? result.success : true,
            category: (result as CategoryProductsResponse).category ?? null,
            data: (result as CategoryProductsResponse).data ?? [],
            meta: (result as CategoryProductsResponse).meta ?? {
                pagination: {
                    page: 1,
                    per_page: 12,
                    total: 0,
                    last_page: 0,
                },
            },
        } as CategoryProductsResponse;
    }
    
    // Hata durumu
    throw new Error('Invalid category products response format');
}

type ProductCategoryBySlugApiPayload = {
    id: number;
    attributes?: {
        name?: string;
        slug?: string;
        description?: string | null;
        type?: string;
    };
};

function mapProductCategoryBySlugPayload(
    payload: ProductCategoryBySlugApiPayload | null | undefined
): CategoryProductsResponse['category'] | null {
    if (!payload?.id) return null;
    const attrs = payload.attributes ?? {};
    return {
        id: payload.id,
        name: attrs.name ?? '',
        slug: attrs.slug ?? '',
        type: attrs.type ?? '',
        description: attrs.description ?? null,
    };
}

/**
 * Slug ile kategori meta (description dahil) — landing sayfaları (group-tours / private-tours) için.
 * Ürün listesi searchProducts ile gelir; açıklama bu endpoint'ten okunur.
 */
export async function getProductCategoryBySlug(
    slug: string,
    language: string,
    serverCache?: ProductSearchServerCacheOptions
): Promise<CategoryProductsResponse['category'] | null> {
    const trimmedSlug = slug.trim();
    const trimmedLang = language.trim();
    if (!trimmedSlug || !trimmedLang) return null;

    const searchParams = new URLSearchParams();
    searchParams.set('slug', trimmedSlug);
    searchParams.set('language', trimmedLang);

    const url = `/product-categories/by-slug?${searchParams.toString()}`;
    const response = await productSearchFetch(url, serverCache);

    if (!response.ok) {
        return null;
    }

    const result = await parseLaravelJsonResponse<
        { data?: ProductCategoryBySlugApiPayload } | ProductCategoryBySlugApiPayload
    >(response, 'Product Category by-slug API');

    if (result && typeof result === 'object' && 'data' in result) {
        return mapProductCategoryBySlugPayload((result as { data?: ProductCategoryBySlugApiPayload }).data);
    }

    return mapProductCategoryBySlugPayload(result as ProductCategoryBySlugApiPayload);
}

/**
 * API hata kontrolü ve tip güvenliği için helper
 */
export function isApiErrorResponse(response: any): response is ApiErrorResponse {
    return response && response.success === false && typeof response.message === 'string';
}
